import React, { useState, useRef } from 'react';
import supportTicketsService from '../../services/supportTicketsService';
import './CreateTicketModal.css';

const TICKET_TYPES = [
  { value: 'Bug', label: 'Bug', icon: '🐛', description: 'Report something that is broken' },
  { value: 'Feature', label: 'Feature', icon: '✨', description: 'Request a new feature' },
  { value: 'Improvement', label: 'Improvement', icon: '💡', description: 'Suggest an improvement' },
  { value: 'Question', label: 'Question', icon: '❓', description: 'Ask a question' }
];

const PRIORITY_LEVELS = [
  { value: 'Low', label: 'Low', color: '#3b82f6' },
  { value: 'Medium', label: 'Medium', color: '#f59e0b' },
  { value: 'High', label: 'High', color: '#ef4444' }
];

const CreateTicketModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    ticket_type: 'Bug',
    priority: 'Medium'
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleTypeSelect = (type) => {
    setFormData({ ...formData, ticket_type: type });
  };

  const handlePrioritySelect = (priority) => {
    setFormData({ ...formData, priority });
  };

  // File handling
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList);
    // Filter for allowed types
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain'
    ];

    const validFiles = newFiles.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        setError(`File type not allowed: ${file.name}`);
        return false;
      }
      if (file.size > 25 * 1024 * 1024) {
        setError(`File too large: ${file.name} (max 25MB)`);
        return false;
      }
      return true;
    });

    setFiles(prev => [...prev, ...validFiles]);
    setError(null);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (type) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type === 'application/pdf') return '📄';
    if (type.includes('word')) return '📝';
    if (type.includes('excel') || type.includes('spreadsheet')) return '📊';
    return '📎';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('subject', formData.subject.trim());
      submitData.append('description', formData.description.trim());
      submitData.append('ticket_type', formData.ticket_type);
      submitData.append('priority', formData.priority);

      // Add files
      files.forEach(file => {
        submitData.append('attachments', file);
      });

      await supportTicketsService.createTicket(submitData);

      setSuccess(true);

      setTimeout(() => {
        // Reset form
        setFormData({
          subject: '',
          description: '',
          ticket_type: 'Bug',
          priority: 'Medium'
        });
        setFiles([]);
        setSuccess(false);
        onSuccess?.();
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create ticket');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        subject: '',
        description: '',
        ticket_type: 'Bug',
        priority: 'Medium'
      });
      setFiles([]);
      setError(null);
      setErrors({});
      setSuccess(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="st-modal-overlay active" onClick={handleClose}>
      <div className="st-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="st-modal-header">
          <div className="st-modal-header-content">
            <div className="st-modal-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <line x1="9" y1="10" x2="15" y2="10"/>
                <line x1="12" y1="7" x2="12" y2="13"/>
              </svg>
            </div>
            <div>
              <h2>Create Support Ticket</h2>
              <p>Report an issue or request a feature</p>
            </div>
          </div>
          <button className="st-modal-close-btn" onClick={handleClose}>&times;</button>
        </div>

        {/* Success Message */}
        {success && (
          <div className="st-success-message">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
              <polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            Ticket created successfully!
          </div>
        )}

        {/* Error Message */}
        {error && <div className="st-error-message">{error}</div>}

        {/* Modal Body */}
        <div className="st-modal-body">
          <form onSubmit={handleSubmit} className="st-form">
            {/* Ticket Type Selection */}
            <div className="st-form-section">
              <label className="st-form-label">What type of ticket is this?</label>
              <div className="st-type-selector">
                {TICKET_TYPES.map(type => (
                  <button
                    key={type.value}
                    type="button"
                    className={`st-type-option ${formData.ticket_type === type.value ? 'selected' : ''}`}
                    onClick={() => handleTypeSelect(type.value)}
                  >
                    <span className="st-type-icon">{type.icon}</span>
                    <span className="st-type-label">{type.label}</span>
                    <span className="st-type-desc">{type.description}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Subject */}
            <div className="st-form-group">
              <label htmlFor="subject" className="st-form-label">
                Subject <span className="required">*</span>
              </label>
              <input
                type="text"
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Brief summary of your issue or request"
                className={errors.subject ? 'input-error' : ''}
                maxLength={255}
              />
              {errors.subject && <span className="st-error-text">{errors.subject}</span>}
            </div>

            {/* Priority */}
            <div className="st-form-group">
              <label className="st-form-label">Priority</label>
              <div className="st-priority-selector">
                {PRIORITY_LEVELS.map(p => (
                  <button
                    key={p.value}
                    type="button"
                    className={`st-priority-option ${formData.priority === p.value ? 'selected' : ''}`}
                    style={{ '--priority-color': p.color }}
                    onClick={() => handlePrioritySelect(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="st-form-group">
              <label htmlFor="description" className="st-form-label">
                Description <span className="required">*</span>
              </label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Please provide as much detail as possible..."
                className={errors.description ? 'input-error' : ''}
                rows="6"
              />
              {errors.description && <span className="st-error-text">{errors.description}</span>}
            </div>

            {/* File Upload */}
            <div className="st-form-group">
              <label className="st-form-label">Attachments</label>
              <div
                className={`st-file-dropzone ${dragActive ? 'active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  style={{ display: 'none' }}
                />
                <div className="st-dropzone-content">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <p><strong>Click to upload</strong> or drag and drop</p>
                  <p className="st-dropzone-hint">Images, PDFs, Documents (max 25MB)</p>
                </div>
              </div>

              {/* File List */}
              {files.length > 0 && (
                <div className="st-file-list">
                  {files.map((file, index) => (
                    <div key={index} className="st-file-item">
                      <span className="st-file-icon">{getFileIcon(file.type)}</span>
                      <div className="st-file-info">
                        <span className="st-file-name">{file.name}</span>
                        <span className="st-file-size">{formatFileSize(file.size)}</span>
                      </div>
                      <button
                        type="button"
                        className="st-file-remove"
                        onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                      >
                        &times;
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </form>
        </div>

        {/* Modal Footer */}
        <div className="st-modal-footer">
          <button
            type="button"
            onClick={handleClose}
            className="st-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="st-btn-submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="st-btn-spinner"></span>
                Creating...
              </>
            ) : (
              'Create Ticket'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateTicketModal;
